import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const logoUrl = "/easycar-logo.webp";

const navLinks = [
  { label: 'Accueil', href: '#accueil' },
  { label: 'Nos véhicules', href: '#vehicules' },
  { label: 'Nos services', href: '#services' },
  { label: 'Financement', href: '#financement' },
  { label: 'Avis clients', href: '#avis' },
  { label: 'Contact', href: '#contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const id = href.replace('#', '');
    if (!isHomePage) {
      navigate(`/${href}`);
      setMobileOpen(false);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else if (id === 'accueil') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const textColor = scrolled ? 'text-foreground-900' : 'text-white';
  const bgClass = scrolled
    ? 'bg-background-50 shadow-md'
    : 'bg-transparent';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}
      >
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a
              href="#accueil"
              onClick={(e) => {
                e.preventDefault();
                if (!isHomePage) {
                  navigate('/');
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                setMobileOpen(false);
              }}
              className="flex items-center gap-2"
            >
              <img decoding="async" height={192} width={192}
                src={logoUrl}
                alt="Easy Car"
                className={`h-10 md:h-12 w-auto rounded-full transition-all ${scrolled ? '' : 'bg-white/90'}`}
              />
              <span className={`font-heading font-bold text-lg md:text-xl hidden sm:block ${textColor}`}>
                Easy Car
              </span>
            </a>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(link.href);
                  }}
                  className={`font-label text-sm font-medium transition-colors hover:text-primary-500 ${textColor}`}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:+262692505551"
                className={`flex items-center gap-2 px-4 py-2 rounded-md border font-label text-sm font-medium transition-all whitespace-nowrap ${
                  scrolled
                    ? 'border-foreground-300 text-foreground-900 hover:border-primary-500 hover:text-primary-500'
                    : 'border-white/50 text-white hover:border-white hover:bg-white/10'
                }`}
              >
                <i className="ri-phone-line w-4 h-4 flex items-center justify-center" />
                Appeler
              </a>
              {isHomePage ? (
                <a
                  href="#vehicules"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/vehicules');
                  }}
                  className="px-5 py-2 rounded-md bg-primary-500 text-white font-label text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
                >
                  Voir tous nos véhicules
                </a>
              ) : null}
            </div>

            {/* Mobile buttons */}
            <div className="flex items-center gap-2 lg:hidden">
              <a
                href="tel:+262692505551"
                className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  scrolled ? 'bg-primary-500 text-white' : 'bg-white/20 text-white'
                }`}
                aria-label="Appeler"
              >
                <i className="ri-phone-line w-5 h-5 flex items-center justify-center" />
              </a>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  scrolled ? 'text-foreground-900' : 'text-white'
                }`}
                aria-label="Menu"
              >
                <i className={`w-5 h-5 flex items-center justify-center ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-secondary-950/95 backdrop-blur-sm transition-all duration-300 lg:hidden ${
          mobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="flex flex-col items-center justify-center h-full gap-6">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(link.href);
              }}
              className="text-white font-heading text-xl font-medium hover:text-primary-500 transition-colors"
            >
              {link.label}
            </a>
          ))}
          <div className="flex flex-col gap-3 mt-4 w-full max-w-xs px-6">
            <a
              href="tel:+262692505551"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-white/30 text-white font-label font-medium hover:bg-white/10 transition-all"
            >
              <i className="ri-phone-line w-5 h-5 flex items-center justify-center" />
              0692 50 55 51
            </a>
            {isHomePage ? (
              <a
                href="#vehicules"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/vehicules');
                  setMobileOpen(false);
                }}
                className="flex items-center justify-center px-4 py-3 rounded-md bg-primary-500 text-white font-label font-semibold hover:bg-primary-600 transition-all"
              >
                Voir tous nos véhicules
              </a>
            ) : (
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                  setMobileOpen(false);
                }}
                className="flex items-center justify-center px-4 py-3 rounded-md bg-primary-500 text-white font-label font-semibold hover:bg-primary-600 transition-all"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center mr-2" />
                Retour à l'accueil
              </a>
            )}
          </div>
          <div className="mt-6 flex gap-4">
            <Link
              to="/mentions-legales"
              onClick={() => setMobileOpen(false)}
              className="text-white/60 text-sm hover:text-white transition-colors"
            >
              Mentions légales
            </Link>
            <Link
              to="/politique-confidentialite"
              onClick={() => setMobileOpen(false)}
              className="text-white/60 text-sm hover:text-white transition-colors"
            >
              Confidentialité
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}